import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (v ? new Date(v).toLocaleString() : 'Not yet');

const Item = ({ label, value, testId }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium" data-testid={testId}>{value}</p>
  </div>
);

export const WebhookHealthPanel = ({ onRenewed }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'system_admin';
  const [state, setState] = useState(null);
  const [renewing, setRenewing] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/ringcentral/webhook-health`).then((r) => setState(r.data)).catch(() => setState({}));
  }, [isAdmin]);

  if (!isAdmin) return null;

  const renew = async () => {
    setRenewing(true);
    try {
      const res = await axios.post(`${API}/ringcentral/webhook-health/renew`);
      setState(res.data.state);
      if (res.data.success) toast.success(res.data.action === 'recreated' ? 'Webhook recreated' : 'Webhook renewed');
      else toast.error(res.data.error || 'Webhook renewal failed');
      onRenewed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Webhook renewal failed');
    } finally {
      setRenewing(false);
    }
  };

  const status = state?.status || 'Unknown';
  return (
    <div className="mb-4 p-4 border rounded-lg" data-testid="webhook-health-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> Automatic renewal (checked hourly)
        </p>
        <Button size="sm" onClick={renew} disabled={renewing} data-testid="renew-webhook-btn">
          {renewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Renew Webhook
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <p className="text-xs text-gray-500">Current Status</p>
          <Badge variant={status === 'Active' ? 'default' : 'destructive'} data-testid="webhook-health-status">{status}</Badge>
        </div>
        <Item label="Last Successful Renewal" value={fmt(state?.last_renewed_at)} testId="webhook-health-last-renewed" />
        <Item label="Next Expiration" value={fmt(state?.expiration_time)} testId="webhook-health-expiration" />
      </div>
      {state?.last_error && (
        <p className="text-xs text-red-600 mt-3" data-testid="webhook-health-error">Last problem: {state.last_error}</p>
      )}
      {state?.last_checked_at && <p className="text-xs text-gray-400 mt-2">Last checked {fmt(state.last_checked_at)}</p>}
    </div>
  );
};
